import { createAsyncThunk } from '@reduxjs/toolkit';
import { iStateMessage } from '@/customTypes/GenericReduxTypes';

import { iGenericResponse } from '@/customTypes/CommonServiceTypes';
import { curryGetThunkName } from '@/projectUtils/ReduxUtil';
import { httpStatusCodes } from '@/customTypes/NetworkTypes';
import {
  userService,
  productService,
  resellerService,
} from '@/services/api/cresivoxServer';

import {
  REDUCER_NAME,
  iUserLoginResponse,
  iChangePassword,
  iProductResponse,
  iUserProfile,
  iUserProfileDetails,
} from './Types';
const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const userLogin = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { email: string; password: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('authLogin'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, { rejectWithValue }) => {
    try {
      const response = await userService.userLogin(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              //   sessionStorage.setItem("user", JSON.stringify(data.data));
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

// developerRegister

export const developerRegister = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { email: string; password: string; mobile: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('authRegister'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, { rejectWithValue }) => {
    try {
      const response = await userService.developerRegister(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data && data.data) {
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const userRegister = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { email: string; password: string; mobile: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('authRegister'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, { rejectWithValue }) => {
    try {
      const response = await userService.userRegister(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data && data.data) {
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const getProfile = async () => {
  try {
    // Call the user profile API
    const response = await userService.userProfileDetails();

    if (response) {
      const { httpStatusCode, data, message } = response;

      switch (httpStatusCode) {
        case httpStatusCodes.SUCCESS_OK: {
          if (data && data.data) {
            return data.data; // Return the profile data
          } else {
            throw new Error('Profile data is missing in the response.');
          }
        }
        default: {
          throw new Error(message || 'Failed to fetch the profile.');
        }
      }
    } else {
      throw new Error('No response received from the server.');
    }
  } catch (error: any) {
    console.error('🚀 ~ Error fetching profile:', error.message);
    throw new Error(
      error.message || 'Something went wrong while fetching the profile.'
    );
  }
};

export const changePassword = createAsyncThunk<
  iGenericResponse<iChangePassword | null> | null,
  { currentPassword: string; newPassword: string; confirmedPassword: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('changePassword'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, { rejectWithValue }) => {
    try {
      const response = await userService.userChangePassword(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const productAdd = createAsyncThunk<
  iGenericResponse<iProductResponse | null> | null,
  {
    title: string;
    sku: string;
    product_type: string;
    category_id: string;
    subCategory: string;
    description: string;
    product_image: string;
    price: number;
    cost_price: number;
    sale_price: number;
    retail_price: number;
    inventory_status: string;
    availability: string;
    images: Array<File | string>;
    customFields?: Array<{ label: string; value: string }>;
    shippingPrice: number;
    internationalshippingPrice: number;
    tags?: Array<string>;
    global_trade_item_number: string;
    manufacturer_part_number: string;
    brand_name: string;
    product_upc_ean: string;
    custom_field_name: string;
    custom_field_value: string;
    is_shipping_free: boolean;
    page_title: string;
    meta_keywords: string;
    meta_description: string;
    product_url: string;
    delivery_event_name: string;
    // variants: Array<{ variant_name: string; variant_value: string }>;
    shipping_details: Array<{ location_name: string; shipping_charge: number }>;
    delivery_from_date: Date;
    delivery_to_date: Date;
    sizes?: string[];
    color_images?: string[];
    about: string;
    taxOptions: string;
    lowStock: string;
    currentStock: string;
    isPackagingCharges: boolean;
    packagingCharges: number;
  },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('productAdd'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, { rejectWithValue }) => {
    try {
      // Call the product service to add a product
      const response = await productService.createProduct(arg);

      if (response) {
        // console.log("this is product add data",response);
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data) {
              // Map iProductDetails to iProductResponse
              const productResponse: iProductResponse = {
                title: data.title,
                sku: data.sku,
                product_type: data.product_type,
                category_id: data.category_id,
                description: data.description,
                product_image: data.product_image,
                price: data.price,
                cost_price: data.cost_price,
                sale_price: data.sale_price,
                retail_price: data.retail_price,
                inventory_status: data.inventory_status,
                availability: data.availability,
                global_trade_item_number: data.global_trade_item_number,
                manufacturer_part_number: data.manufacturer_part_number,
                brand_name: data.brand_name, // corrected: was 'string', should be 'data.brand_name'
                product_upc_ean: data.product_upc_ean,
                custom_field_name: data.custom_field_name,
                custom_field_value: data.custom_field_value,
                is_shipping_free: data.is_shipping_free,
                page_title: data.page_title,
                meta_keywords: data.meta_keywords,
                meta_description: data.meta_description,
                product_url: data.product_url,
                delivery_event_name: data.delivery_event_name,
                // variants: data.variants || [],  // Ensure that variants exist or fall back to empty array
                shipping_details: data.shipping_details || [],
                taxOptions: data.taxOptions || null,
                // international_Shipping:data.international_Shippint,
              };

              return {
                httpStatusCode,
                data: productResponse,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message: 'Product data is missing',
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received from the server',
        });
      }
    } catch (error) {
      console.error('Error in productAdd thunk:', error);
      return rejectWithValue({
        message: 'Something went wrong while adding the product',
      });
    }
  }
);

export const getUserProfileInfo = createAsyncThunk<
  iGenericResponse<iUserProfile | null> | null,
  void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('getUserProfile'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, { rejectWithValue }) => {
    try {
      const response = await userService.userProfileDetails();
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              const user = data.data.user;

              // ---- store only required keys in localStorage ----
              try {
                if (user.deliveryState) {
                  localStorage.setItem(
                    'deliveryState',
                    JSON.stringify(JSON.parse(user.deliveryState))
                  );
                }
                if (user.deliveryLocations) {
                  localStorage.setItem(
                    'deliveryLocations',
                    JSON.stringify(JSON.parse(user.deliveryLocations))
                  );
                }
              } catch (e) {
                console.error('Failed to parse delivery data', e);
              }

              return {
                data: {
                  httpStatusCode,
                  user,
                  message,
                },
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }

          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const updateUserProfile = createAsyncThunk<
  iGenericResponse<iUserProfileDetails | null> | null,
  iUserProfileDetails,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('updateUserProfile'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (payload, { rejectWithValue }) => {
    try {
      const safePayload: iUserProfileDetails = {
        ...payload,
        deliveryState: Array.isArray(payload.deliveryState)
          ? payload.deliveryState
          : [],
        deliveryLocations: Array.isArray(payload.deliveryLocations)
          ? payload.deliveryLocations
          : [],
      };

      const response = await userService.updateUserProfile(safePayload);

      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && (data as any).data) {
              return {
                data: {
                  httpStatusCode,
                  user: (data as any).data,
                  message,
                },
              };
            } else {
              return rejectWithValue({ httpStatusCode, message });
            }
          }
          default:
            return rejectWithValue({ httpStatusCode, message });
        }
      }

      return rejectWithValue({ message: 'No response received' });
    } catch (error) {
      return rejectWithValue({ message: 'Something went wrong' });
    }
  }
);

export const markOnboardingComplete = createAsyncThunk<
  iGenericResponse<{ message: string } | null> | null,
  void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('markOnboardingComplete'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (_arg, { rejectWithValue }) => {
    try {
      const response = await userService.markOnboardingComplete();

      if (response) {
        const { httpStatusCode, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            return { data: { httpStatusCode, message } };
          }
          default:
            return rejectWithValue({ httpStatusCode, message });
        }
      }

      return rejectWithValue({ message: 'No response received' });
    } catch (error) {
      return rejectWithValue({ message: 'Something went wrong' });
    }
  }
);

export const activateDeactivateUser = createAsyncThunk<
  iGenericResponse<iUserProfileDetails | null> | null,
  { userId: string; status: boolean },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('activateDeactivateUser'),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async ({ userId, status }, { rejectWithValue }) => {
    try {
      const response = await userService.activateDeactivateUser(userId, status);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  httpStatusCode,
                  user: data.data,
                  message,
                },
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const resellerGoogleLogin = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { code?: string; credential?: string; autoSignup?: boolean },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('resellerGoogleLogin'),

  async (arg, { rejectWithValue }) => {
    try {
      const response = await resellerService.resellerLoginWithGoogle(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: // auto sign-up when the Google email had no account
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              } as any;
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const resellerGoogleLink = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { linkToken: string; password?: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('resellerGoogleLink'),

  async (arg, { rejectWithValue }) => {
    try {
      const response = await resellerService.resellerLinkGoogle(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: // auto sign-up when the Google email had no account
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              } as any;
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const resellerFacebookLogin = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { accessToken: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('resellerFacebookLogin'),

  async (arg, { rejectWithValue }) => {
    try {
      const response = await resellerService.resellerLoginWithFacebook(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              } as any;
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const resellerFacebookSignup = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { accessToken: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('resellerFacebookSignup'),

  async (arg, { rejectWithValue }) => {
    try {
      const response = await resellerService.resellerSignupWithFacebook(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data && data.data) {
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              } as any;
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const resellerGoogleSignup = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { code?: string; credential?: string; role?: 'VENDOR' | 'WHOLESALER' | 'DEVELOPER' },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('resellerGoogleSignup'),

  async (arg, { rejectWithValue }) => {
    try {
      const response = await resellerService.resellerSignupWithGoogle(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data && data.data) {
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              } as any;
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);

export const resellerGoogleCompleteSignup = createAsyncThunk<
  iGenericResponse<iUserLoginResponse | null> | null,
  { signupToken: string; role: 'VENDOR' | 'WHOLESALER' | 'DEVELOPER'; mobile: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName('resellerGoogleCompleteSignup'),

  async (arg, { rejectWithValue }) => {
    try {
      const response = await resellerService.resellerCompleteGoogleSignup(arg);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_CREATED: {
            if (data && data.data) {
              localStorage.setItem('VendorId', data.data.id);
              localStorage.setItem('userToken', data.data.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              return {
                httpStatusCode,
                data: data.data,
                message,
              } as any;
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: 'No response received',
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: 'Something went wrong',
      });
    }
  }
);
